import { useCallback, useEffect, useState } from 'react';
import { useTrimesters } from '../../trimesters/context/TrimestersContext';
import { classroomService, SaveClassroomInput } from '../services/classroom.service';
import { ClassroomRecord } from '../types/classroom.types';

export const useClassrooms = (trimesterId?: string) => {
  const { classes, setClasses } = useTrimesters();
  const [classrooms, setClassrooms] = useState<ClassroomRecord[]>(classes);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClassrooms = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const items = await classroomService.list(trimesterId);
      setClassrooms(items);
      setClasses(items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar as turmas.');
    } finally {
      setIsLoading(false);
    }
  }, [setClasses, trimesterId]);

  useEffect(() => {
    loadClassrooms();
  }, [loadClassrooms]);

  const createClassroom = async (input: SaveClassroomInput) => {
    const classroom = await classroomService.create({
      ...input,
      trimesterId: input.trimesterId ?? trimesterId,
    });
    setClassrooms((current) => [classroom, ...current]);
    setClasses((current) => [classroom, ...current]);

    return classroom;
  };

  const updateClassroom = async (classroomId: string, input: SaveClassroomInput) => {
    const classroom = await classroomService.update(classroomId, {
      ...input,
      trimesterId: input.trimesterId ?? trimesterId,
    });
    setClassrooms((current) => current.map((item) => (item.id === classroomId ? classroom : item)));
    setClasses((current) => current.map((item) => (item.id === classroomId ? classroom : item)));

    return classroom;
  };

  const removeClassroom = async (classroomId: string) => {
    await classroomService.remove(classroomId);
    setClassrooms((current) => current.filter((item) => item.id !== classroomId));
    setClasses((current) => current.filter((item) => item.id !== classroomId));
  };

  return {
    classrooms,
    isLoading,
    error,
    reloadClassrooms: loadClassrooms,
    createClassroom,
    updateClassroom,
    removeClassroom,
  };
};
